import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { toast } from "sonner";
import {
  CalendarCheck2,
  XCircle,
  CalendarClock,
  Maximize2,
  Minimize2,
  CheckCircle2,
  Clock,
  AlertTriangle,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  getAppointmentStats,
  getAppointmentPunctuality,
  getPatientGeoDistribution,
  getPatientRetention,
  getDoctorLeaveImpact,
} from "../../api/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import SegmentedControl from "../../components/SegmentedControl";
import { Input } from "../../components/ui/input";
import PatientGeoMap from "../../components/PatientGeoMap";
import { cn } from "../../lib/utils";

const ISO = "YYYY-MM-DD";

const RANGE_PRESETS = [
  { value: "today", label: "Today", range: () => [moment().format(ISO), moment().format(ISO)] },
  {
    value: "yesterday",
    label: "Yesterday",
    range: () => [moment().subtract(1, "day").format(ISO), moment().subtract(1, "day").format(ISO)],
  },
  {
    value: "week",
    label: "This Week",
    range: () => [moment().startOf("week").format(ISO), moment().endOf("week").format(ISO)],
  },
  {
    value: "lastWeek",
    label: "Last Week",
    range: () => [
      moment().subtract(1, "week").startOf("week").format(ISO),
      moment().subtract(1, "week").endOf("week").format(ISO),
    ],
  },
  {
    value: "month",
    label: "This Month",
    range: () => [moment().startOf("month").format(ISO), moment().endOf("month").format(ISO)],
  },
  {
    value: "lastMonth",
    label: "Last Month",
    range: () => [
      moment().subtract(1, "month").startOf("month").format(ISO),
      moment().subtract(1, "month").endOf("month").format(ISO),
    ],
  },
  {
    value: "year",
    label: "This Year",
    range: () => [moment().startOf("year").format(ISO), moment().endOf("year").format(ISO)],
  },
  // Deliberately blank, not "not computed yet" — every from/to consumer
  // (the effects below, filterParams) treats an empty pair as "no date
  // restriction" rather than skipping the fetch.
  { value: "all", label: "All Time", range: () => ["", ""] },
  { value: "custom", label: "Custom", range: null },
];

// Total = every booking made for that day, regardless of its current status
// (so it isn't a stack of cancelled+rescheduled — those are two different
// outcomes a booking can land in later, not parts of a whole).
const SERIES = [
  { key: "total", label: "Total Bookings", varName: "--info", icon: CalendarCheck2 },
  { key: "cancelled", label: "Cancelled", varName: "--destructive", icon: XCircle },
  { key: "rescheduled", label: "Rescheduled", varName: "--warning", icon: CalendarClock },
];

// Arriving early or a few minutes late is normal clinic noise, not a
// problem worth flagging — only real lateness earns a status color, so the
// scatter's eye is drawn to the appointments that actually ran off schedule
// instead of every single dot competing for attention.
// A delay only ever gets shown as a plain minute count elsewhere in this
// file — past 60 that reads as a wall of digits ("+900m") instead of a
// duration a person parses at a glance ("+15h").
const formatDuration = (mins) => {
  const abs = Math.round(Math.abs(mins));
  if (abs < 60) return `${abs}m`;
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

// Arriving early or a few minutes late is normal clinic noise, not a
// problem worth flagging — only real lateness earns a status color, so the
// eye is drawn to the days that actually ran off schedule instead of every
// appointment competing for attention.
const PUNCTUALITY_BUCKETS = [
  { key: "onTime", label: "On time (within 5m)", test: (d) => d <= 5, varName: "--muted-foreground", icon: CheckCircle2 },
  { key: "late", label: "Late (5–30m)", test: (d) => d > 5 && d <= 30, varName: "--warning", icon: Clock },
  { key: "veryLate", label: "Very late (30m+)", test: (d) => d > 30, varName: "--destructive", icon: AlertTriangle },
];

// The chart's X axis carries fractional values (each dot jitters slightly
// off its day's integer position), so this reads by *day* rather than
// trying to resolve one exact dot — a day summary is more robust than a
// tooltip that has to guess which overlapping point you meant, and it's
// the same information either way (every point that day, plus the
// average).
function PunctualityTrendTooltip({ active, label, daySummaries }) {
  if (!active || label === undefined || label === null) return null;
  const day = daySummaries[Math.round(label)];
  if (!day) return null;
  const checkedIn = day.onTime + day.late + day.veryLate;
  return (
    <div className="rounded-md border border-border bg-popover p-3 text-xs shadow-md">
      <p className="mb-1.5 font-medium text-popover-foreground">{moment(day.date, ISO).format("D MMM YYYY")}</p>
      <div className="space-y-1">
        {PUNCTUALITY_BUCKETS.map((b) => (
          <div key={b.key} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <span className="h-2 w-2 rounded-full" style={{ background: `hsl(var(${b.varName}))` }} />
              {b.label}
            </span>
            <span className="font-medium tabular-nums text-popover-foreground">{day[b.key]}</span>
          </div>
        ))}
        <div className="mt-1.5 flex items-center justify-between gap-4 border-t border-border pt-1.5 text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-3 rounded-full border-t-2 border-dashed border-foreground/60" />
            Day average
          </span>
          <span className="font-medium tabular-nums text-popover-foreground">
            {day.avgDelay === 0
              ? "On time"
              : day.avgDelay > 0
                ? `${formatDuration(day.avgDelay)} late`
                : `${formatDuration(day.avgDelay)} early`}
          </span>
        </div>
        <div className="flex items-center justify-between gap-4 text-muted-foreground">
          <span>Checked in</span>
          <span className="font-medium tabular-nums text-popover-foreground">{checkedIn}</span>
        </div>
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-border bg-popover p-3 text-xs shadow-md">
      <p className="mb-1.5 font-medium text-popover-foreground">{moment(label, ISO).format("D MMM YYYY")}</p>
      <div className="space-y-1">
        {SERIES.map((s) => {
          const row = payload.find((p) => p.dataKey === s.key);
          if (!row) return null;
          return (
            <div key={s.key} className="flex items-center justify-between gap-4">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <span className="h-2 w-2 rounded-full" style={{ background: `hsl(var(${s.varName}))` }} />
                {s.label}
              </span>
              <span className="font-medium tabular-nums text-popover-foreground">{row.value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Stats() {
  const [preset, setPreset] = useState("week");
  const defaultWeek = RANGE_PRESETS.find((p) => p.value === "week").range();
  const [customFrom, setCustomFrom] = useState(defaultWeek[0]);
  const [customTo, setCustomTo] = useState(defaultWeek[1]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [punctuality, setPunctuality] = useState(null);
  const [punctualityLoading, setPunctualityLoading] = useState(true);
  const [geo, setGeo] = useState(null);
  const [geoLoading, setGeoLoading] = useState(true);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [retention, setRetention] = useState(null);
  const [retentionLoading, setRetentionLoading] = useState(true);
  const [leaveImpact, setLeaveImpact] = useState([]);
  const [leaveImpactLoading, setLeaveImpactLoading] = useState(true);

  const [from, to] = useMemo(() => {
    if (preset === "custom") return [customFrom, customTo];
    return RANGE_PRESETS.find((p) => p.value === preset)?.range() || defaultWeek;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, customFrom, customTo]);

  // "All Time" deliberately produces an empty from/to (no date restriction),
  // which is different from Custom mode having incomplete fields the user
  // hasn't finished typing yet — only the latter should hold off fetching.
  const ready = preset !== "custom" || (!!customFrom && !!customTo);

  // Shared by every card on the page (Bookings, Punctuality, the map) so
  // they all agree on what "this period" means. Blank from/to (All Time)
  // are sent as undefined so the backend applies no date filter at all,
  // rather than an impossible empty-string range.
  const filterParams = useMemo(() => ({ from: from || undefined, to: to || undefined }), [from, to]);

  useEffect(() => {
    if (!ready) return;
    setLoading(true);
    getAppointmentStats(filterParams)
      .then((res) => setData(res.data))
      .catch((err) => toast.error(err?.message || "Could not load stats."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams, ready]);

  useEffect(() => {
    if (!ready) return;
    setPunctualityLoading(true);
    getAppointmentPunctuality(filterParams)
      .then((res) => setPunctuality(res.data))
      .catch((err) => toast.error(err?.message || "Could not load check-in punctuality."))
      .finally(() => setPunctualityLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams, ready]);

  // Where patients live, scoped the same way as the charts above — "this
  // period's" patients, via whether they had a matching appointment (see
  // geoDistribution on the backend). Sharing filterParams keeps the whole
  // page answering one consistent question instead of the map being an
  // unfiltered outlier.
  useEffect(() => {
    if (!ready) return;
    setGeoLoading(true);
    getPatientGeoDistribution(filterParams)
      .then((res) => setGeo(res.data))
      .catch((err) => toast.error(err?.message || "Could not load patient locations."))
      .finally(() => setGeoLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams, ready]);

  useEffect(() => {
    if (!ready) return;
    setRetentionLoading(true);
    getPatientRetention(filterParams)
      .then((res) => setRetention(res.data))
      .catch((err) => toast.error(err?.message || "Could not load patient retention."))
      .finally(() => setRetentionLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams, ready]);

  useEffect(() => {
    if (!ready) return;
    setLeaveImpactLoading(true);
    getDoctorLeaveImpact(filterParams)
      .then((res) => setLeaveImpact(res.data || []))
      .catch((err) => toast.error(err?.message || "Could not load doctor leave impact."))
      .finally(() => setLeaveImpactLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams, ready]);

  const totals = useMemo(() => data?.totals || { total: 0, cancelled: 0, rescheduled: 0 }, [data]);
  const pct = (n) => (totals.total ? Math.round((n / totals.total) * 100) : 0);

  // Rolls the map's per-pincode points up to district level — a district a
  // patient's pincode belongs to, not a second server round-trip. The map
  // itself plots these (one marker per district, at a count-weighted
  // centroid of its pincodes) rather than the raw per-pincode points, so
  // its bubble number is always exactly a district's count — matching this
  // same list — instead of whatever a screen-pixel-radius cluster happens
  // to sweep in at the current zoom.
  const districtPoints = useMemo(() => {
    const byDistrict = new Map();
    for (const p of geo?.points || []) {
      const name = p.district ? `${p.district}, ${p.state}` : p.state || "Unknown";
      const entry = byDistrict.get(name) || { name, count: 0, latSum: 0, lngSum: 0 };
      entry.count += p.count;
      entry.latSum += p.lat * p.count;
      entry.lngSum += p.lng * p.count;
      byDistrict.set(name, entry);
    }
    return [...byDistrict.values()]
      .map((d) => ({ name: d.name, count: d.count, lat: d.latSum / d.count, lng: d.lngSum / d.count }))
      .sort((a, b) => b.count - a.count);
  }, [geo]);
  const topDistricts = useMemo(() => districtPoints.slice(0, 6), [districtPoints]);
  const maxDistrictCount = topDistricts[0]?.count || 1;

  const punctualityPoints = useMemo(() => punctuality || [], [punctuality]);

  // Headline counts for the summary tiles — the answer at a glance,
  // without having to read the chart at all.
  const punctualityTotals = useMemo(() => {
    const totals = { onTime: 0, late: 0, veryLate: 0 };
    for (const p of punctualityPoints) {
      const bucket = PUNCTUALITY_BUCKETS.find((b) => b.test(p.delay_minutes)) || PUNCTUALITY_BUCKETS[0];
      totals[bucket.key] += 1;
    }
    return totals;
  }, [punctualityPoints]);
  const punctualityPct = (n) => (punctualityPoints.length ? Math.round((n / punctualityPoints.length) * 100) : 0);
  const overallAvgDelay = useMemo(
    () =>
      punctualityPoints.length
        ? Math.round(punctualityPoints.reduce((s, p) => s + p.delay_minutes, 0) / punctualityPoints.length)
        : 0,
    [punctualityPoints]
  );

  // Combines the old scatter's per-appointment precision with the bar
  // chart's day-over-day pattern in one view: every appointment is still
  // its own dot — jittered sideways within its day's column so a busy day
  // doesn't collapse into an overlapping blob — but plotted by *delay*
  // instead of raw clock time, so "0" reads as on time by construction
  // instead of needing a diagonal reference line to mentally subtract
  // against. The day's average delay is overlaid as a connecting line, so
  // the daily trend (what the bar chart showed) is visible at the same
  // time as the spread within each day (what the scatter showed).
  const { scatterByBucket, daySummaries, dayDomain, dayTicks, trendDelta } = useMemo(() => {
    const groups = new Map();
    for (const p of punctualityPoints) {
      if (!groups.has(p.date)) groups.set(p.date, []);
      groups.get(p.date).push(p);
    }
    const sortedDates = [...groups.keys()].sort();
    const dateIndex = new Map(sortedDates.map((d, i) => [d, i]));

    const jittered = [];
    const summaries = sortedDates.map((date, i) => {
      const pts = groups.get(date);
      const n = pts.length;
      const counts = { onTime: 0, late: 0, veryLate: 0 };
      let delaySum = 0;
      pts.forEach((p, idx) => {
        const jitter = n <= 1 ? 0 : (idx / (n - 1) - 0.5) * 0.7;
        jittered.push({ ...p, x: dateIndex.get(date) + jitter });
        const bucket = PUNCTUALITY_BUCKETS.find((b) => b.test(p.delay_minutes)) || PUNCTUALITY_BUCKETS[0];
        counts[bucket.key] += 1;
        delaySum += p.delay_minutes;
      });
      return { x: i, date, ...counts, avgDelay: Math.round(delaySum / n) };
    });

    const byBucket = PUNCTUALITY_BUCKETS.map((b) => ({ ...b, points: jittered.filter((p) => b.test(p.delay_minutes)) }));

    // At most ~7 x-axis labels regardless of range length — "This Month"
    // shouldn't try to print 30 date labels.
    const tickCount = Math.min(sortedDates.length, 7);
    const ticks =
      tickCount <= 1
        ? sortedDates.map((_, i) => i)
        : [
            ...new Set(
              Array.from({ length: tickCount }, (_, i) => Math.round((i * (sortedDates.length - 1)) / (tickCount - 1)))
            ),
          ];

    // Second half vs first half of the range's average delay — a rough
    // "getting better or worse" signal for the header, not a rigorous
    // regression. Needs at least 4 days on both sides to mean anything.
    let delta = null;
    if (summaries.length >= 4) {
      const mid = Math.floor(summaries.length / 2);
      const avg = (rows) => rows.reduce((s, r) => s + r.avgDelay, 0) / rows.length;
      delta = Math.round(avg(summaries.slice(mid)) - avg(summaries.slice(0, mid)));
    }

    return {
      scatterByBucket: byBucket,
      daySummaries: summaries,
      dayDomain: [-0.5, Math.max(0, sortedDates.length - 1) + 0.5],
      dayTicks: ticks,
      trendDelta: delta,
    };
  }, [punctualityPoints]);

  const dayLabel = (x) => {
    const day = daySummaries[Math.round(x)];
    return day ? moment(day.date, ISO).format("D MMM") : "";
  };

  // Recharts needs every bar's numeric fields present, or a day with zero
  // activity draws a gap instead of a zero-height bar.
  const trend = useMemo(
    () =>
      (data?.trend || []).map((d) => ({
        date: d.date,
        total: d.total || 0,
        cancelled: d.cancelled || 0,
        rescheduled: d.rescheduled || 0,
      })),
    [data]
  );

  // The same three numbers as the stat tiles above, just as a share of the
  // whole instead of three separate counts — "cancelled" and "rescheduled"
  // are each a subset of "total", not parts that sum to it on their own, so
  // the remainder (still active or completed) fills out the rest of the pie.
  const composition = useMemo(() => {
    const other = Math.max(0, totals.total - totals.cancelled - totals.rescheduled);
    return [
      { key: "active", label: "Completed / active", value: other, varName: "--info" },
      { key: "cancelled", label: "Cancelled", value: totals.cancelled, varName: "--destructive" },
      { key: "rescheduled", label: "Rescheduled", value: totals.rescheduled, varName: "--warning" },
    ].filter((d) => d.value > 0);
  }, [totals]);

  // Canned reasons chosen at cancel time — see cancelAppointmentSchema on
  // the backend, which has required a note (but not necessarily a
  // disposition) since this session, so a "Not specified" bucket is
  // expected for older or note-only cancellations.
  const cancellationReasons = useMemo(() => data?.cancellationReasons || [], [data]);
  const maxCancellationReason = Math.max(1, ...cancellationReasons.map((r) => r.count));

  const VISIT_TYPE_META = {
    center: { label: "Center Visit", varName: "--info" },
    video: { label: "Online", varName: "--secondary" },
    home: { label: "Home Visit", varName: "--warning" },
    walkin: { label: "Walk In", varName: "--destructive" },
  };
  const visitTypeBreakdown = useMemo(
    () =>
      (data?.visitTypeBreakdown || []).map((d) => ({
        key: d.visitType,
        label: VISIT_TYPE_META[d.visitType]?.label || d.visitType,
        value: d.count,
        varName: VISIT_TYPE_META[d.visitType]?.varName || "--muted-foreground",
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );
  const visitTypeTotal = visitTypeBreakdown.reduce((s, d) => s + d.value, 0) || 1;

  const maxLeaveImpact = Math.max(1, ...leaveImpact.map((d) => d.cancelledForUnavailability));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-3xl font-semibold">Stats</h2>
        <p className="text-sm text-muted-foreground">Where your patients live, and how bookings are trending</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <SegmentedControl items={RANGE_PRESETS} value={preset} onChange={setPreset} />
        {preset === "custom" && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border bg-muted/30 p-2">
            <label htmlFor="stats-from" className="text-xs font-medium text-muted-foreground">
              From
            </label>
            <Input
              id="stats-from"
              type="date"
              value={customFrom}
              max={customTo}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="h-8 w-[10rem] bg-card text-sm"
            />
            <label htmlFor="stats-to" className="text-xs font-medium text-muted-foreground">
              To
            </label>
            <Input
              id="stats-to"
              type="date"
              value={customTo}
              min={customFrom}
              onChange={(e) => setCustomTo(e.target.value)}
              className="h-8 w-[10rem] bg-card text-sm"
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {SERIES.map((s) => {
          const Icon = s.icon;
          const value = totals[s.key];
          return (
            <Card key={s.key}>
              <CardContent className="flex items-center gap-3 p-5">
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md"
                  style={{ background: `hsl(var(${s.varName}) / 0.1)`, color: `hsl(var(${s.varName}))` }}
                >
                  <Icon className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                  <p className="font-serif text-xl font-semibold">
                    {loading ? <Skeleton className="h-6 w-10" /> : value}
                  </p>
                  {!loading && s.key !== "total" && (
                    <p className="text-xs text-muted-foreground">{pct(value)}% of total</p>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Patients by location</CardTitle>
        </CardHeader>
        <CardContent className={cn("pt-2", geoLoading && "opacity-50")}>
          {geoLoading ? (
            <Skeleton className="h-[26rem] w-full" />
          ) : !geo?.points?.length ? (
            <p className="py-16 text-center text-sm text-muted-foreground">
              No patients with a mapped pincode in this range.
            </p>
          ) : (
            <>
              {mapExpanded && (
                <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setMapExpanded(false)} />
              )}
              <div
                className={cn(
                  "grid grid-cols-1 gap-4",
                  mapExpanded
                    ? "fixed inset-4 z-50 grid-rows-[1fr_auto] rounded-lg border border-border bg-card p-4 shadow-2xl lg:grid-cols-[1fr_260px] lg:grid-rows-1"
                    : "lg:grid-cols-[1fr_260px]"
                )}
              >
                <div
                  className={cn(
                    "relative w-full overflow-hidden rounded-md border border-border",
                    mapExpanded ? "h-full" : "h-[26rem]"
                  )}
                >
                  <button
                    type="button"
                    onClick={() => setMapExpanded((v) => !v)}
                    aria-label={mapExpanded ? "Exit fullscreen" : "View fullscreen"}
                    className="absolute right-3 top-3 z-[1000] flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card text-foreground shadow-sm transition-colors hover:bg-muted"
                  >
                    {mapExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                  </button>
                  <PatientGeoMap points={districtPoints} expanded={mapExpanded} />
                </div>
                <div
                  className={cn(
                    "flex flex-col gap-3 overflow-y-auto rounded-md border border-border bg-muted/20 p-4",
                    mapExpanded ? "h-full" : "h-[26rem]"
                  )}
                >
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Top districts</p>
                  {topDistricts.map((d, i) => (
                    <div key={d.name} className="space-y-1">
                      <div className="flex items-center justify-between gap-2 text-sm">
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-info/10 text-[11px] font-semibold text-info">
                            {i + 1}
                          </span>
                          <span className="truncate font-medium">{d.name}</span>
                        </span>
                        <span className="shrink-0 font-serif text-sm font-semibold tabular-nums">{d.count}</span>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-info"
                          style={{ width: `${Math.max(6, (d.count / maxDistrictCount) * 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-col items-start gap-2 sm:flex-row sm:items-center">
          <CardTitle>Bookings over time</CardTitle>
          <div className="flex flex-wrap items-center gap-3">
            {SERIES.map((s) => (
              <span key={s.key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: `hsl(var(${s.varName}))` }} />
                {s.label}
              </span>
            ))}
          </div>
        </CardHeader>
        <CardContent className={cn("pt-2", loading && "opacity-50")}>
          {loading ? (
            <Skeleton className="h-72 w-full" />
          ) : trend.length === 0 ? (
            <p className="py-16 text-center text-sm text-muted-foreground">No appointments in this range.</p>
          ) : (
            <div className="flex flex-col gap-6 lg:flex-row">
              <div className="h-72 flex-1">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trend} margin={{ top: 8, right: 8, left: -16, bottom: 0 }} barGap={2}>
                    <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(d) => moment(d, ISO).format("D MMM")}
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                      axisLine={{ stroke: "hsl(var(--border))" }}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      width={32}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
                    {SERIES.map((s) => (
                      <Bar
                        key={s.key}
                        dataKey={s.key}
                        name={s.label}
                        fill={`hsl(var(${s.varName}))`}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={28}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="flex shrink-0 flex-col items-center gap-3 border-t border-border pt-6 lg:w-56 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
                <p className="self-start text-xs font-medium uppercase tracking-wide text-muted-foreground lg:self-center">
                  Overall total
                </p>
                <div className="relative h-40 w-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={composition}
                        dataKey="value"
                        nameKey="label"
                        innerRadius={48}
                        outerRadius={72}
                        paddingAngle={composition.length > 1 ? 2 : 0}
                        stroke="hsl(var(--card))"
                        strokeWidth={2}
                      >
                        {composition.map((d) => (
                          <Cell key={d.key} fill={`hsl(var(${d.varName}))`} />
                        ))}
                      </Pie>
                      <Tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0].payload;
                          return (
                            <div className="rounded-md border border-border bg-popover p-2 text-xs shadow-md">
                              <span className="font-medium text-popover-foreground">
                                {d.label}: {d.value}
                              </span>
                            </div>
                          );
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <span className="font-serif text-xl font-semibold">{totals.total}</span>
                    <span className="text-[11px] text-muted-foreground">total</span>
                  </div>
                </div>
                <div className="w-full space-y-1.5">
                  {composition.map((d) => (
                    <div key={d.key} className="flex items-center justify-between gap-2 text-xs">
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <span className="h-2 w-2 rounded-full" style={{ background: `hsl(var(${d.varName}))` }} />
                        {d.label}
                      </span>
                      <span className="font-medium tabular-nums">{pct(d.value)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-col items-start gap-1">
          <CardTitle>Patient punctuality</CardTitle>
          {!punctualityLoading && punctualityPoints.length > 0 && (
            <p className="text-sm text-muted-foreground">
              {overallAvgDelay === 0 ? (
                <>
                  On average, patients checked in{" "}
                  <span className="font-medium text-foreground">right on schedule</span>.
                </>
              ) : (
                <>
                  On average, patients checked in{" "}
                  <span className="font-medium text-foreground">
                    {overallAvgDelay > 0 ? `${formatDuration(overallAvgDelay)} after` : `${formatDuration(overallAvgDelay)} before`}
                  </span>{" "}
                  their scheduled slot.
                </>
              )}
              {trendDelta !== null && Math.abs(trendDelta) >= 2 && (
                <>
                  {" "}
                  <span className={cn("font-medium", trendDelta > 0 ? "text-destructive" : "text-secondary")}>
                    {trendDelta > 0 ? "Running later" : "Improving"} over the period.
                  </span>
                </>
              )}
            </p>
          )}
        </CardHeader>
        <CardContent className={cn("space-y-4 pt-2", punctualityLoading && "opacity-50")}>
          {punctualityLoading ? (
            <Skeleton className="h-80 w-full" />
          ) : punctualityPoints.length === 0 ? (
            <p className="py-16 text-center text-sm text-muted-foreground">
              No checked-in appointments in this range.
            </p>
          ) : (
            <>
              {/* The headline, before the chart — how many arrived on time
                  vs late, at a glance, no chart-reading required. */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {PUNCTUALITY_BUCKETS.map((b) => {
                  const Icon = b.icon;
                  const value = punctualityTotals[b.key];
                  return (
                    <div key={b.key} className="flex items-center gap-3 rounded-md border border-border p-4">
                      <span
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md"
                        style={{ background: `hsl(var(${b.varName}) / 0.1)`, color: `hsl(var(${b.varName}))` }}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <div>
                        <p className="text-xs text-muted-foreground">{b.label}</p>
                        <p className="font-serif text-xl font-semibold">{value}</p>
                        <p className="text-xs text-muted-foreground">{punctualityPct(value)}% of checked-in</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Every checked-in appointment as its own dot — jittered
                  sideways within its day so a busy day fans out instead of
                  overlapping into a blob — plotted by how late it ran
                  rather than by raw clock time, so "0" is on time by
                  construction and no diagonal reference line is needed.
                  The dashed line is each day's average, layered on top so
                  the trend reads at a glance alongside the day's spread. */}
              <div className="flex flex-wrap items-center gap-3">
                {PUNCTUALITY_BUCKETS.map((b) => (
                  <span key={b.key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="h-2 w-2 rounded-full" style={{ background: `hsl(var(${b.varName}))` }} />
                    {b.label}
                  </span>
                ))}
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="h-0 w-3 border-t-2 border-dashed border-foreground/50" />
                  Day average
                </span>
              </div>

              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid stroke="hsl(var(--border))" />
                    <XAxis
                      type="number"
                      dataKey="x"
                      domain={dayDomain}
                      ticks={dayTicks}
                      tickFormatter={dayLabel}
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                      axisLine={{ stroke: "hsl(var(--border))" }}
                      tickLine={false}
                      allowDuplicatedCategory={false}
                    />
                    <YAxis
                      type="number"
                      dataKey="delay_minutes"
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                      tickFormatter={(v) => (v === 0 ? "On time" : v > 0 ? `+${formatDuration(v)}` : `-${formatDuration(v)}`)}
                      axisLine={false}
                      tickLine={false}
                      width={76}
                    />
                    <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" />
                    <Tooltip
                      content={<PunctualityTrendTooltip daySummaries={daySummaries} />}
                      cursor={{ stroke: "hsl(var(--border))", strokeDasharray: "3 3" }}
                    />
                    {scatterByBucket.map((b) => (
                      <Scatter key={b.key} name={b.label} data={b.points} fill={`hsl(var(${b.varName}))`} fillOpacity={0.75} r={3.5} />
                    ))}
                    <Line
                      data={daySummaries}
                      dataKey="avgDelay"
                      name="Day average"
                      stroke="hsl(var(--foreground) / 0.55)"
                      strokeWidth={2}
                      strokeDasharray="5 4"
                      dot={{ r: 3, fill: "hsl(var(--foreground))", strokeWidth: 0 }}
                      activeDot={false}
                      isAnimationActive={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Why appointments get cancelled</CardTitle>
          <p className="text-sm text-muted-foreground">
            Reasons chosen at cancel time, and how much of it traces back to doctor leave.
          </p>
        </CardHeader>
        <CardContent className={cn("pt-2", loading && "opacity-50")}>
          {loading ? (
            <Skeleton className="h-56 w-full" />
          ) : cancellationReasons.length === 0 ? (
            <p className="py-16 text-center text-sm text-muted-foreground">No cancellations in this range.</p>
          ) : (
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="space-y-3">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Cancellation reasons</p>
                {cancellationReasons.map((r) => (
                  <div key={r.reason} className="space-y-1">
                    <div className="flex items-center justify-between gap-2 text-sm">
                      <span className="truncate font-medium">{r.reason}</span>
                      <span className="shrink-0 font-serif text-sm font-semibold tabular-nums">{r.count}</span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-destructive"
                        style={{ width: `${Math.max(6, (r.count / maxCancellationReason) * 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-3 border-t border-border pt-6 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Doctor leave impact</p>
                {leaveImpactLoading ? (
                  <Skeleton className="h-40 w-full" />
                ) : leaveImpact.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No doctor leave or leave-related cancellations in this range.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {leaveImpact.slice(0, 6).map((row) => (
                      <div key={row.doctorId} className="space-y-1">
                        <div className="flex items-center justify-between gap-2 text-sm">
                          <span className="truncate font-medium">{row.doctorName}</span>
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {row.leaveCount} leave {row.leaveCount === 1 ? "period" : "periods"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full rounded-full bg-destructive"
                              style={{
                                width: row.cancelledForUnavailability
                                  ? `${Math.max(6, (row.cancelledForUnavailability / maxLeaveImpact) * 100)}%`
                                  : "0%",
                              }}
                            />
                          </div>
                          <span className="shrink-0 font-serif text-sm font-semibold tabular-nums">
                            {row.cancelledForUnavailability}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Patients &amp; visit mix</CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="grid gap-6 lg:grid-cols-2">
            <div>
              <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                New vs. returning patients
              </p>
              {retentionLoading ? (
                <Skeleton className="h-24 w-full" />
              ) : !retention || retention.total === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">No patients in this range.</p>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-md border border-border p-4">
                    <p className="text-xs text-muted-foreground">New patients</p>
                    <p className="font-serif text-xl font-semibold">{retention.new}</p>
                    <p className="text-xs text-muted-foreground">
                      {Math.round((retention.new / retention.total) * 100)}% of patients
                    </p>
                  </div>
                  <div className="rounded-md border border-border p-4">
                    <p className="text-xs text-muted-foreground">Returning patients</p>
                    <p className="font-serif text-xl font-semibold">{retention.returning}</p>
                    <p className="text-xs text-muted-foreground">
                      {Math.round((retention.returning / retention.total) * 100)}% of patients
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-border pt-6 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
              <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">Visit type mix</p>
              {loading ? (
                <Skeleton className="h-24 w-full" />
              ) : visitTypeBreakdown.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">No appointments in this range.</p>
              ) : (
                <div className="flex items-center gap-6">
                  <div className="h-28 w-28 shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={visitTypeBreakdown}
                          dataKey="value"
                          nameKey="label"
                          innerRadius={34}
                          outerRadius={52}
                          paddingAngle={visitTypeBreakdown.length > 1 ? 2 : 0}
                          stroke="hsl(var(--card))"
                          strokeWidth={2}
                        >
                          {visitTypeBreakdown.map((d) => (
                            <Cell key={d.key} fill={`hsl(var(${d.varName}))`} />
                          ))}
                        </Pie>
                        <Tooltip
                          content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const d = payload[0].payload;
                            return (
                              <div className="rounded-md border border-border bg-popover p-2 text-xs shadow-md">
                                <span className="font-medium text-popover-foreground">
                                  {d.label}: {d.value}
                                </span>
                              </div>
                            );
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 space-y-1.5">
                    {visitTypeBreakdown.map((d) => (
                      <div key={d.key} className="flex items-center justify-between gap-2 text-xs">
                        <span className="flex items-center gap-1.5 text-muted-foreground">
                          <span className="h-2 w-2 rounded-full" style={{ background: `hsl(var(${d.varName}))` }} />
                          {d.label}
                        </span>
                        <span className="font-medium tabular-nums">
                          {Math.round((d.value / visitTypeTotal) * 100)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
